import { filter, get, isEmpty, omit, difference, keys, find, compact, uniqBy, reduce } from 'lodash'

import {
  ADD_SIGNATURE_TO_ORDER,
  ADD_TIP_TO_ORDER,
  ADD_TO_UNSYNCED_ORDER_LIST,
  CLEAR_ORDER_SEARCH,
  CLEAR_SELECTED_ORDER,
  CLOSE_TAB_ORDER_WITH_CASH,
  CREATE_OFFLINE_ORDER_FAILED,
  CREATE_OFFLINE_ORDER_SUCCEEDED,
  FILTER_ORDERS,
  ORDER_DETAILS_REQUESTED,
  LOAD_ORDER_DETAILS_FAILED,
  LOAD_ORDER_DETAILS_SUCCEEDED,
  LOAD_ORDERS_DELTAS_FAILED,
  LOAD_ORDERS_DELTAS_SUCCEEDED,
  LOAD_PRE_ORDERS_FAILED,
  LOAD_PRE_ORDERS_SUCCEEDED,
  PRE_ORDERS_REQUESTED,
  PURGE_ORDERS,
  REPLAY_AUTH_REQUEST_FAILED,
  REPLAY_AUTH_REQUEST_SUCCEEDED,
  SAVE_OFFLINE_ORDER,
  SAVE_ORDER,
  SEARCH_ORDER,
  SELECT_ORDER,
  SPLIT_ORDER_REQUESTED,
  SPLIT_ORDER_SUCCEEDED,
  SPLIT_ORDER_FAILED,
  SYNC_ORDER_FAILED,
  SYNC_ORDER_INITIATED,
  SYNC_ORDER_SUCCEEDED,
  UPDATE_ORDER_EXEMPTION_OVERRIDES,
  UPDATE_ORDER_FAILED,
  UPDATE_ORDER_SUCCEEDED,
  VOID_ORDER_FAILED,
  VOID_ORDER_SUCCEEDED,
  MUTATION_REQUESTED,
  MUTATION_SUCCEEDED,
  MUTATION_FAILED,
} from '../actions/order'

import { UPDATE_USER_NOTES_SUCCEEDED } from '../actions/orders/patch'

import {
  UPDATE_EXEMPTIONS_REQUESTED,
  UPDATE_EXEMPTIONS_SUCCEEDED,
  UPDATE_EXEMPTIONS_FAILED,
} from '../actions/orderTotalRemote'

import { LOGOUT } from '../actions/attendant'

import {
  ADJUST_LINE_ITEM_QUANTITIES_SUCCEEDED,
  ADJUST_LINE_ITEM_QUANTITIES_FAILED,
} from '../actions/orders/adjustments'
import { COMBINE_ORDERS_FAILED } from '../actions/orders/combine'

import { SAVE_RICH_CHECKOUT_ORDER } from '../actions/richCheckout/order'
import { REQUEST_REFUND } from '../actions/refund'

import { MUTATION_TYPES } from '../remote/mutationApi'

import PreOrdersItemViewModel from '../viewmodels/PreOrdersItemViewModel'

import OrderStates from '../utils/orderStates'
import { toast } from 'react-toastify'

const safeAddToArray = (array, value) => {
  const arr = array || []

  if (arr.indexOf(value) < 0) return arr.concat(value)

  return arr
}

const safeRemoveFromArray = (array, value) => {
  const arr = array || []

  return filter(arr, (x) => x !== value)
}

const reducer = (state = {}, action) => {
  switch(action.type) {
    case UPDATE_USER_NOTES_SUCCEEDED: {
      const { uuid, userNotes } = action.payload

      return {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: {
            ...state.byId[uuid],
            userNotes,
          },
        }
      }
    }

    case UPDATE_EXEMPTIONS_REQUESTED: {
      const orderId = action.payload?.orderUuid

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            pendingMutation: MUTATION_TYPES.updateTotals,
          },
        }
      }
    }

    case UPDATE_EXEMPTIONS_SUCCEEDED: {
      const orderId = action.payload?.orderUuid

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            pendingMutation: undefined,
          },
        }
      }
    }

    case UPDATE_EXEMPTIONS_FAILED: {
      const orderId = action.payload?.orderUuid

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            pendingMutation: undefined,
          },
        }
      }
    }

    case SPLIT_ORDER_REQUESTED: {
      const { orderId, newOrderUuid } = action.payload
      const newOrder = state.byId[newOrderUuid]
      const originalOrder = state.byId[orderId]

      return {
        ...state,
        byId: {
          ...state.byId,
          [newOrderUuid]: {
            ...newOrder,
            pendingMutation: MUTATION_TYPES.split,
          },
          [orderId]: {
            ...originalOrder,
            pendingMutation: MUTATION_TYPES.split,
          }
        }
      }
    }

    case SPLIT_ORDER_SUCCEEDED: {
      const { originalOrderUuid, newOrderUuid } = action.payload
      const newOrder = state.byId[newOrderUuid]
      const originalOrder = state.byId[originalOrderUuid]

      return {
        ...state,
        byId: {
          ...state.byId,
          [newOrderUuid]: {
            ...newOrder,
            pendingMutation: undefined,
          },
          [originalOrderUuid]: {
            ...originalOrder,
            pendingMutation: undefined,
          }
        }
      }
    }

    case SPLIT_ORDER_FAILED: {
      const { originalOrderUuid, newOrderUuid } = action.payload
      const newOrder = state.byId[newOrderUuid]
      const originalOrder = state.byId[originalOrderUuid]

      return {
        ...state,
        byId: {
          ...state.byId,
          [newOrderUuid]: {
            ...newOrder,
            pendingMutation: undefined,
          },
          [originalOrderUuid]: {
            ...originalOrder,
            pendingMutation: undefined,
          }
        }
      }
    }

    case MUTATION_REQUESTED: {
      const { orderId, mutationType } = action.payload
      const order = state.byId[orderId]

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...order,
            pendingMutation: MUTATION_TYPES[mutationType],
          }
        }
      }
    }

    case MUTATION_SUCCEEDED: {
      const { orderId } = action.payload
      const order = state.byId[orderId]

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...order,
            pendingMutation: undefined,
          }
        }
      }
    }

    case MUTATION_FAILED: {
      const { orderId } = action.payload
      const order = state.byId[orderId]

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...order,
            pendingMutation: undefined,
          }
        }
      }
    }

    case CREATE_OFFLINE_ORDER_SUCCEEDED: {
      const { uuid } = action
      const order = state.byId[uuid]

      return {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            errorMessage: null,
            isSyncing: false,
            wasCreatedInStadium: true,
          }
        }
      }
    }

    case CREATE_OFFLINE_ORDER_FAILED: {
      const { uuid } = action
      const order = state.byId[uuid]

      return {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            wasCreatedInStadium: false,
            isSyncing: false,
          }
        }
      }
    }

    case LOGOUT:
      return {
        preOrdersAreLoading: false,
        initialPreOrderLoadComplete: false
      }

    case CLEAR_ORDER_SEARCH:
      return {
        ...state,
        ordersSearch: '',
      }

    case CLEAR_SELECTED_ORDER:
      return {
        ...state,
        selectedOrderId: null,
      }

    case FILTER_ORDERS:
      return {
        ...state,
        ordersFilter: action.payload,
      }

    case REPLAY_AUTH_REQUEST_SUCCEEDED: {
      const order = get(action, 'payload.order', {})
      const id = order.uuid
      const newOrder = state.byId[id]

      const replayStatus = get(action, 'payload.replayStatus', { statusDisplay: 'No info while checking status' })

      // next step is to sync with stadium, which has been kicked off in the saga
      const unsyncedLocalIds = safeAddToArray(state.unsyncedLocalIds, id)
      const offlineCardIds = safeRemoveFromArray(state.offlineCardIds, id)

      return {
        ...state,
        offlineCardIds,
        unsyncedLocalIds,
        byId: {
          ...state.byId,
          [id]: {
            ...newOrder,
            replayStatus,
          }
        }
      }
    }

    case REPLAY_AUTH_REQUEST_FAILED: {
      const order = get(action, 'payload.order', {})
      const id = order.uuid
      const replayStatus = get(action, 'payload.replayStatus', { statusDisplay: 'No info while checking status' })

      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: {
            ...state.byId[id],
            isSyncing: false,
            replayStatus,
          }
        }
      }
    }

    case SAVE_OFFLINE_ORDER: {
      const { uuid } = action.payload

      return {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: action.payload,
        },
        currentOrderId: uuid,
      }
    }

    case ADD_TO_UNSYNCED_ORDER_LIST: {
      const uuid  = action.payload

      let unsyncedLocalIds = [...(state.unsyncedLocalIds || [])]
      unsyncedLocalIds = safeAddToArray(state.unsyncedLocalIds, uuid)
      return {
        ...state,
        unsyncedLocalIds,
      }
    }

    case SAVE_ORDER: {
      const { uuid } = action.payload
      const cardReaderData = {
        ...get(state, `byId.${uuid}.cardReaderData`, {}),
        ...get(action, 'payload.cardReaderData', {}),
      }

      if (!isEmpty(action?.payload?.cardReaderData)) {
        cardReaderData.cardholderName = action?.payload?.cardReaderData?.cardholderName ?? ''
      }

      const nextState = {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: {
            ...get(state, `byId.${uuid}`, {}),
            ...action.payload,
            cardReaderData,
            modifiedAt: new Date(),
          },
        },
        currentOrderId: uuid,
      }

      return nextState
    }

    case SAVE_RICH_CHECKOUT_ORDER: {
      const { uuid } = action.payload

      const successRemoteIds = safeAddToArray(state.successRemoteIds, uuid)

      return {
        ...state,
        successRemoteIds,
        byId: {
          ...state.byId,
          [uuid]: {
            ...action.payload,
            isRichCheckout: true,
            modifiedAt: new Date(),
            syncedAt: new Date(),
          },
        },
        currentOrderId: uuid,
      }
    }

    case SEARCH_ORDER:
      return {
        ...state,
        ordersSearch: action.payload,
      }

    case SELECT_ORDER:
      return {
        ...state,
        selectedOrderId: action.payload,
      }

    case SYNC_ORDER_INITIATED: {
      const id = action.id
      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: {
            ...state.byId[id],
            isSyncing: true,
          }
        }
      }
    }

    case SYNC_ORDER_SUCCEEDED: {
      const order = action.order || {}
      const id = order.uuid

      const successRemoteIds = safeAddToArray(state.successRemoteIds, id)
      const unsyncedLocalIds = safeRemoveFromArray(state.unsyncedLocalIds, id)

      return {
        ...state,
        unsyncedLocalIds,
        successRemoteIds,
        byId: {
          ...state.byId,
          [id]: {
            ...order,
            errorMessage: null,
            isSyncing: false,
            syncedAt: new Date(),
            orderTotalFromOrder: { },
            lineItems: [ ],
            createOrderParams: {
              ...state.byId[id].createOrderParams,
              receiptSignature: null,
            },
          }
        }
      }
    }

    case SYNC_ORDER_FAILED: {
      let order = action.order || {}
      const id = order.uuid
      order.isSyncing = false

      const response = get(action, 'payload.response', {})
      let errorMessage = get(response, 'data.errorMessage', null)

      if (get(action, 'payload.message') === 'Network Error') {
        errorMessage = 'Network error while trying to submit order'
      }

      order.errorMessage = errorMessage

      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: order,
        }
      }
    }

    case UPDATE_ORDER_SUCCEEDED: {
      const { uuid } = action
      const order = state.byId[uuid]

      const successRemoteIds = safeAddToArray(state.successRemoteIds, uuid)
      const unsyncedLocalIds = safeRemoveFromArray(state.unsyncedLocalIds, uuid)

      // move from unsyncedLocalIds to Completed
      return {
        ...state,
        unsyncedLocalIds,
        successRemoteIds,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            createOrderParams: {
              ...state.byId[uuid].createOrderParams,
              receiptSignature: null,
            },
            errorMessage: null,
            isSyncing: false,
            syncedAt: new Date(),
          }
        }
      }
    }

    case UPDATE_ORDER_FAILED: {
      const { uuid } = action
      const order = state.byId[uuid]

      return {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            isSyncing: false,
          }
        }
      }
    }

    case VOID_ORDER_SUCCEEDED: {
      const id = action.orderId
      const order = state.byId[id]
      const voidResponse = action.freedomPayResponse

      if (isEmpty(order)) return state

      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: {
            ...order,
            voidResponse,
            voidedAt: new Date(),
            voidFailedAt: null,
          }
        }
      }
    }

    case VOID_ORDER_FAILED: {
      const id = action.orderId
      const order = state.byId[id]

      // transtype is SALE for void failures. not sure it matters but just in case...
      const voidResponse = {
        ...action.freedomPayResponse,
        transType: "VOID"
      }

      if (isEmpty(order)) return state

      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: {
            ...order,
            voidResponse,
            voidFailedAt: new Date(),
          }
        }
      }
    }

    case ADD_SIGNATURE_TO_ORDER:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.payload.orderUuid]: {
            ...state.byId[action.payload.orderUuid],
            createOrderParams: {
              ...state.byId[action.payload.orderUuid].createOrderParams,
              receiptSignature: action.payload.signature,
            }
          }
        }
      }

    case ADD_TIP_TO_ORDER: {
      const orderUuid = get(action, 'payload.orderUuid', '')
      const tipAmountInCents = get(action, 'payload.tipAmountInCents', 0)

      const order = get(state, `byId.${orderUuid}`, {})
      const amountInCents = get(order, `amountInCents`, 0)
      const newTotal = tipAmountInCents + amountInCents

      const { createOrderParams } = order

      // not handling split payment types.
      let newPayment = get(createOrderParams, ['payments', 0], {})
      newPayment.amountInCents = newTotal

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderUuid]: {
            ...state.byId[orderUuid],
            amountInCents: newTotal,
            tipAmountInCents: action.payload.tipAmountInCents,
            createOrderParams: {
              ...state.byId[orderUuid].createOrderParams,
              payments: [ newPayment ],
              tipAmountInCents: action.payload.tipAmountInCents,
            },
          }
        }
      }
    }

    case PURGE_ORDERS: {
      return {
        ...state,
        byId: {
          ...omit(state.byId, action.payload.orderIds),
        },
        successRemoteIds: difference(state.successRemoteIds, action.payload.orderIds),
        purgedAt: new Date(),
      }
    }

    case REQUEST_REFUND: {
      const orderId = get(action, 'payload.orderId', '')
      const refundItemIndexes = get(action, 'payload.itemIndexes', [])
      const orderMenuItems = [...get(state, `byId.${orderId}.createOrderParams.orderMenuItems`, [])]
      const newOrderMenuItems = [...orderMenuItems.map((item, index) => {
        const isRefundItem = refundItemIndexes.includes(index)
        if (isRefundItem) return { ...item, refund: true }
        return item
      })]
      const newItemModels = [...state.byId[orderId].itemModels.map((item, index) => {
        const isRefundItem = refundItemIndexes.includes(index)
        if (isRefundItem) return { ...item, refund: true }
        return item
      })]

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            createOrderParams: {
              ...state.byId[orderId].createOrderParams,
              orderMenuItems: [...newOrderMenuItems]
            },
            itemModels: newItemModels,
            refundable: true,
          }
        },
      }
    }

    case CLOSE_TAB_ORDER_WITH_CASH: {
      const orderId = get(action, 'payload.orderId', '')

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            cardReaderData: {},
            isRemotePayment: false,
          }
        }
      }
    }

    case PRE_ORDERS_REQUESTED: {
      return {
        ...state,
        preOrdersAreLoading: true
      }
    }

    case LOAD_PRE_ORDERS_SUCCEEDED: {
      const orders = state.byId
      const preOrders = get(action, 'payload', {})

      const updatedLocalOrders = reduce(orders, (updatedOrders, order) => {
        updatedOrders[order?.uuid] = {
          ...orders[order?.uuid],
          ...(preOrders[order?.uuid] ?? {})
        }
        return updatedOrders
      }, {})

      const mergedOrders = { ...preOrders, ...updatedLocalOrders }

      keys(mergedOrders).forEach((orderId) => {
        const localItemModels = get(orders, `${orderId}.itemModels`, [])
        const remoteItemModels = get(preOrders, `${orderId}.itemModels`, [])

        // If this order doesn't have item models, or if there is no matching preorder, skip the next steps
        if (isEmpty(localItemModels) || isEmpty(remoteItemModels)) return

        let mergedItemModels = remoteItemModels.map((remoteItemModel) => {
          const maybedMatchItemModel = find(localItemModels, ['lineItemUuid', remoteItemModel.lineItemUuid])

          if (maybedMatchItemModel !== undefined && maybedMatchItemModel.refund === true) {
            return maybedMatchItemModel
          }

          if (maybedMatchItemModel !== undefined) {
            return remoteItemModel
          }

          return undefined
        })

        const cleanMergedItems = compact([ ...localItemModels, ...mergedItemModels ])

        mergedOrders[orderId].itemModels = uniqBy(cleanMergedItems, 'lineItemUuid')
        mergedOrders[orderId].syncedAt = new Date()
        mergedOrders[orderId].latestFromServer = true
      })

      // Reset the remote syncing data when all orders are pulled since this is a complete sync.
      return {
        ...state,
        byId: {
          ...state.byId,
          ...mergedOrders
        },
        orderDeltasSyncSuccessAt: null,
        unsyncedRemoteIds: [],
        preOrdersAreLoading: false,
        initialPreOrderLoadComplete: true
      }
    }

    case LOAD_PRE_ORDERS_FAILED: {
      return {
        ...state
      }
    }

    case LOAD_ORDERS_DELTAS_SUCCEEDED: {
      const orders = state.byId
      let deltaOrders = get(action, 'payload.dataDeltas', [])
      let unsyncedRemoteIds = [...(state.unsyncedRemoteIds || [])]
      deltaOrders.forEach((order) => {
        if (!orders[order.uuid]) {
          orders[order.uuid] = {}
        }
        unsyncedRemoteIds = safeAddToArray(unsyncedRemoteIds, order.uuid)
        orders[order.uuid].isSyncing = true
        delete orders[order.uuid].syncedAt
      })

      let newTimestamp = get(action, 'payload.nextSinceTimestamp', null)
      return {
        ...state,
        orderDeltasSyncSuccessAt: newTimestamp === null ? new Date() : new Date(newTimestamp),
        unsyncedRemoteIds,
      }
    }

    case LOAD_ORDERS_DELTAS_FAILED: {
      return {
        ...state,
      }
    }

    case ORDER_DETAILS_REQUESTED: {
      const orderId = get(action, 'payload.orderUuid', '')

      // Consider the request for details to be a pending mutation so we block any normal flows.
      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            pendingMutation: MUTATION_TYPES.identity,
            latestFromServer: false, 
          },
        }
      }
    }

    case LOAD_ORDER_DETAILS_SUCCEEDED: {
      const orders = state.byId
      let order = get(action, 'payload', {})
      order.latestFromServer = true
      order.isSyncing = false
      order.syncedAt = new Date()

      // if placed on the same device, we have some data stored locally that we want to keep around
      // TODO - need to rely on backend for payment types and such
      if (OrderStates.isRemovable(order.state)) {
        delete orders[order.uuid]
      } else {
        const localOrder = orders[order.uuid]

        orders[order.uuid] = {
          ...localOrder,
          ...order,
          pendingMutation: undefined,
        }
      }

      let unsyncedRemoteIds = safeRemoveFromArray(state.unsyncedRemoteIds, order.uuid)

      return {
        ...state,
        byId: {
          ...state.byId
        },
        unsyncedRemoteIds
      }
    }

    case LOAD_ORDER_DETAILS_FAILED: {
      const orders = state.byId
      const orderUuid = get(action, 'payload.orderUuid', '')

      orders[orderUuid] = {
        ...orders[orderUuid],
        latestFromServer: false,
        isSyncing: false,
        syncedAt: null,
        pendingMutation: undefined,
      }

      return {
        ...state,
        byId: {
          ...state.byId
        }
      }
    }

    case UPDATE_ORDER_EXEMPTION_OVERRIDES: {
      const orderId = get(action, 'payload.orderId', '')
      const exemptionOverrides = get(action, 'payload.exemptionOverrides', {})

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...get(state, `byId.${orderId}`, {}),
            exemptionOverrides: {
              ...get(state, `byId.${orderId}.exemptionOverrides`, {}),
              ...exemptionOverrides,
            }
          }
        }
      }
    }

    case ADJUST_LINE_ITEM_QUANTITIES_SUCCEEDED: {
      const orderId = get(action, 'payload.orderId')
      const order = get(state, ['byId', orderId], {})

      const mutations = get(action, 'payload.data.orderMutations', [])
      const addedLineItemModels = get(action, 'payload.lineItemsToAdd', [])
      const removedLineItemUuids = get(action, 'payload.lineItemsToRemove', [])

      let newLineItems = order.lineItems
      let newItemModels = order.itemModels

      mutations.forEach(mutation => {
        if (mutation.type === "Order::Mutation::AddLineItems") {
          const newLineItemUuids = addedLineItemModels.map(im => im.uuid)

          const newBackendLineItems = filter(mutation.lineItems, li => newLineItemUuids.includes(li.uuid))
          newLineItems = [ ...newLineItems, ...newBackendLineItems ]

          const newFrontendLineItems = addedLineItemModels.map(li => PreOrdersItemViewModel(li))
          newItemModels = [ ...newItemModels, ...newFrontendLineItems ]
        }

        if (mutation.type === "Order::Mutation::Refund") {
          newLineItems = filter(newLineItems, (li) => !removedLineItemUuids.includes(li.uuid))
          newItemModels = filter(newItemModels, (im) => !removedLineItemUuids.includes(im.lineItemUuid))
        }
      })

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...order,
            itemModels: newItemModels,
            lineItems: newLineItems,
          },
        },
      }
    }

    // not going to retry these for now; just storing them for posterity
    case ADJUST_LINE_ITEM_QUANTITIES_FAILED: {
      const orderId = get(action, 'payload.orderId')
      const order = get(state, ['byId', orderId], {})

      const failedMutations = get(order, 'failedMutations', [])
      failedMutations.push({
        lineItemsToAdd: get(action, 'payload.lineItemsToAdd', []),
        lineItemsToRemove: get(action, 'payload.lineItemsToRemove', []),
        responseData: get(action, 'payload.response.data', {}),
        responseStatus: get(action, 'payload.response.status'),
        type: 'adjustLineItemQuantities',
      })

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...order,
            failedMutations,
          },
        },
      }
    }

    // not going to retry these for now; just storing them for posterity
    case COMBINE_ORDERS_FAILED: {
      const orderId = action?.payload?.destinationOrderUuid
      const order = get(state, ['byId', orderId])

      if (!order) { return }

      const failedMutations = get(order, 'failedMutations', [])
      failedMutations.push({
        combinedOrderUuid: action?.payload?.combinedOrderUuid,
        responseData: action?.payload?.response?.data ?? {},
        responseStatus: action?.payload?.response?.status,
        type: 'combineOrders',
      })

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...order,
            failedMutations,
          },
        },
      }
    }

    default:
      return state

  }
}

export default reducer
